"use client";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import AuthSocial from "../AuthSocial";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { signUpBusiness, signUpMember, signUpPta } from "@/config/routes";
import { LoginData } from "@/types/Requests/login";
import { config } from "@/config/config";
import { PasswordViewIcon } from "../Icons/PasswordView";
import { PasswordIcon } from "../Icons/Password";
import SmallWarning from "../Alerts/small-warning";

const FormLogin = () => {
  const [unauthenticated, setUnauthenticated] = useState(false);

  useEffect(() => {
    if (sessionStorage) {
      if (!unauthenticated && sessionStorage.getItem("unauthenticated")) {
        setUnauthenticated(true);
      }
    }
  }, [unauthenticated]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const router = useRouter();
  const [viewPassword, setViewPassword] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [error, setError] = useState<
    false | { message: string; errors: { message: string }[] }
  >(false);
  const [urlCallBack, setUrlCallBack] = useState("");
  const searchParams = useSearchParams();
  const pathname = usePathname();
  useEffect(() => {
    const urlCallBackQuery = searchParams.get("urlCallBack");
    if (urlCallBackQuery !== null) {
      setUrlCallBack(urlCallBackQuery);
      router.replace(pathname);
    }
  }, [pathname, router, urlCallBack, searchParams]);

  const onSubmit = async (data: any) => {
    sessionStorage.removeItem("unauthenticated");
    if (showLoader) return;
    setShowLoader(true);
    try {
      const userData: LoginData = {
        email: data.email,
        password: data.password,
      };

      const { signIn } = await import("next-auth/react");

      const logged: any = await signIn("credentials", {
        ...userData,
        redirect: false,
      });

      if (logged.ok) {
        const { redirectByRole } = await import("@/utils/authOptions");
        const { getSession } = await import("next-auth/react");

        const session = await getSession();
        const role = session?.user?.role?.guard_name;

        redirectByRole(role, router.push);
        setShowLoader(false);
      } else {
        setError(JSON.parse(logged.error));
        throw new Error(logged.error);
      }
      setShowLoader(false);
    } catch (error: any) {
      setShowLoader(false);
      console.error(error);
    }
  };

  return (
    <>
      {unauthenticated && (
        <SmallWarning text="Your session expired, please log in again." />
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <label className="mb-2.5 block font-medium ">Enter your email</label>
          <div className="relative">
            <input
              type="email"
              data-test-id="login-email"
              placeholder="Enter your email"
              {...register("email", {
                required: { value: true, message: "Email is required" },
              })}
              className="w-full rounded-lg border border-stroke bg-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark  dark:focus:border-primary"
            />

            <span className="absolute right-4 top-4 opacity-50 dark:opacity-100">
              <svg
                className="fill-current"
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g>
                  <path
                    d="M19.2516 3.30005H2.75156C1.58281 3.30005 0.585938 4.26255 0.585938 5.46567V16.6032C0.585938 17.7719 1.54844 18.7688 2.75156 18.7688H19.2516C20.4203 18.7688 21.4172 17.8063 21.4172 16.6032V5.4313C21.4172 4.26255 20.4203 3.30005 19.2516 3.30005ZM19.2516 4.84692C19.2859 4.84692 19.3203 4.84692 19.3547 4.84692L11.0016 10.2094L2.64844 4.84692C2.68281 4.84692 2.71719 4.84692 2.75156 4.84692H19.2516ZM19.2516 17.1532H2.75156C2.40781 17.1532 2.13281 16.8782 2.13281 16.5344V6.35942L10.1766 11.5157C10.4172 11.6875 10.6922 11.7563 10.9672 11.7563C11.2422 11.7563 11.5172 11.6875 11.7578 11.5157L19.8016 6.35942V16.5688C19.8703 16.9125 19.5953 17.1532 19.2516 17.1532Z"
                    fill=""
                  />
                </g>
              </svg>
            </span>
          </div>
          {errors.email && (
            <div className="text-danger p-2 text-lg">
              <span>{errors.email.message as string}</span>
            </div>
          )}
        </div>

        <div>
          <label className="mb-2.5 block font-medium">
            Enter your Password
          </label>
          <div className="relative">
            <input
              type={!viewPassword ? "password" : "text"}
              data-test-id="login-password"
              placeholder="Enter your password"
              {...register("password", {
                required: { value: true, message: "Password is required" },
              })}
              className="w-full rounded-lg border border-stroke bg-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:focus:border-primary"
            />

            <span
              className="absolute right-4 top-4 cursor-pointer"
              onClick={() => {
                setViewPassword(!viewPassword);
              }}
            >
              {viewPassword ? (
                <PasswordViewIcon className="w-6 h-6 opacity-50 dark:opacity-100" />
              ) : (
                <PasswordIcon className="w-6 h-6 opacity-50 dark:opacity-100" />
              )}
            </span>
          </div>
          {errors.password && (
            <div className="text-danger p-2 text-lg">
              <span>{errors.password.message as string}</span>
            </div>
          )}
        </div>

        {error ? (
          <div className="text-danger p-2 text-lg">
            <span>{error.message} </span>
          </div>
        ) : (
          <></>
        )}
        <div className="flex items-center justify-between flex-wrap my-6">
          <div className="flex items-center mb-4">
            <label className="text-sm font-medium text-gray-900 dark:text-gray-300 flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 me-3 text-black bg-gray-100 border-gray-300 rounded focus:ring-gray-900 dark:focus:ring-gray dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              Remember me
            </label>
          </div>
          <Link
            href={`${config.backend_url}/forgot-password`}
            className="mb-4 ml-4 text-secondary font-bold"
          >
            Recover password
          </Link>
        </div>

        <div className="mb-5">
          <button
            type="submit"
            data-test-id="login-continue"
            className="flex w-full cursor-pointer text-white border-primary rounded-lg font-bold border bg-primary p-3 transition hover:bg-opacity-90 justify-center"
          >
            {showLoader ? (
              <svg
                className="animate-spin -ml-1 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <span>Sign In</span>
            )}
          </button>
        </div>
        {/* <div className="relative flex py-5 items-center">
        <div className="flex-grow border-t border-bodydark2"></div>
        <span className="flex-shrink mx-4 text-gray-400">or</span>
        <div className="flex-grow border-t border-bodydark2"></div>
      </div>
      <AuthSocial /> */}
        <div className="w-full mt-6 text-center">
          <h2 className="font-bold">
            I don{`'`}t have an account.{" "}
            <Link
              href={
                urlCallBack == "member"
                  ? signUpMember
                  : urlCallBack == "pta"
                    ? signUpPta
                    : urlCallBack == "business"
                      ? signUpBusiness
                      : "/"
              }
              className="text-secondary"
            >
              Sign up
            </Link>
          </h2>
        </div>
      </form>
    </>
  );
};

export default FormLogin;
